import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import LauraInsightsCard from '../../components/Admin/LauraInsightsCard';
import MemberAccountModal from '../../components/Admin/MemberAccountModal';
import './BridgeClubAdmin.css';

interface BridgeClubMember {
  id: number;
  member_number: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  joined_at: string;
  linked_store_name: string | null;
  loyalty_punches: number;
  // Local (Kettle Falls) members are on the POINTS program; null for
  // retail-partner members (who keep the punch card).
  local_points_balance?: number | null;
  animals_count: number;
  total_purchases: number;
  last_purchase_date: string | null;
  is_active: boolean;
  email_verified: boolean;
  // Editable-field extras (added by _serialize_bc_member_card + list endpoint)
  zip_code?: string;
  local_access?: boolean;
  home_location_id?: number | null;
  home_location_name?: string | null;
}

interface EditMemberModalState {
  isOpen: boolean;
  member: BridgeClubMember | null;
  loading: boolean;
  error: string | null;
  fieldErrors: Record<string, string>;
}

interface EditFormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  zip_code: string;
  city: string;
  state: string;
  home_location: string; // '' = none; otherwise RetailLocation id as string
  local_access: boolean;
}

interface RetailLocationOption {
  id: number;
  name: string;
}

interface ResetPasswordModalState {
  isOpen: boolean;
  member: BridgeClubMember | null;
  loading: boolean;
  result: {
    type: 'success' | 'error';
    message: string;
    temporaryPassword?: string;
  } | null;
}

interface DeleteMemberModalState {
  isOpen: boolean;
  member: BridgeClubMember | null;
  loading: boolean;
  error: string | null;
}

interface BridgeClubStore {
  id: number;
  name: string;
  city: string;
  state: string;
  member_count: number;
  purchases_count: number;
  total_revenue: number;
  store_code?: string;
  qr_urls?: {
    floor: string;
    register: string;
    shelf: string;
    general: string;
  };
}

interface NetworkAnalytics {
  total_members: number;
  new_members_this_month: number;
  active_members: number;
  total_purchases: number;
  purchases_this_month: number;
  loyalty_redemptions: number;
  total_revenue: number;
  revenue_this_month: number;
  top_stores: BridgeClubStore[];
  top_products: Array<{ name: string; count: number }>;
  competitor_analytics?: {
    total_competitor_items: number;
    competitor_revenue: number;
    top_competitor_brands: Array<{ brand: string; count: number; revenue: number }>;
  };
}

type TabType = 'overview' | 'members' | 'stores' | 'purchases' | 'walk-ins' | 'tax-requests';

interface TaxExemptionRequestRow {
  id: number;
  member_name: string;
  member_email: string;
  certificate_number: string | null;
  issuing_state: string;
  expiration_date: string;
  member_notes: string;
  certificate_url: string | null;
  original_filename: string;
  exemption_type_display: string;
  status: string;
  status_display: string;
  staff_notes: string;
  created_at: string;
}

interface WalkInRow {
  id: number;
  cardholder_name: string;
  card_last_four: string;
  card_type: string;
  visit_count: number;
  total_spend: string;
  first_visit: string | null;
  last_visit: string | null;
  invite_sent_at: string | null;
  invite_email: string | null;
  promoted_to_member_id: number | null;
  promoted_at: string | null;
}

const BridgeClubAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [members, setMembers] = useState<BridgeClubMember[]>([]);
  const [stores, setStores] = useState<BridgeClubStore[]>([]);
  const [analytics, setAnalytics] = useState<NetworkAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState<'' | 'verified' | 'unverified'>('');
  // Members tab: toggle between full Bridge Club list and local-access-only.
  // Local-only mode fetches /admin/bridge-club/local-members/ which is a
  // different endpoint shape, surfacing local_points.balance per member.
  const [membersScope, setMembersScope] = useState<'all' | 'local'>('all');
  const [localMembers, setLocalMembers] = useState<Array<BridgeClubMember & { local_points?: { balance: number } | null }>>([]);

  // Tax Requests tab state
  const [taxRequests, setTaxRequests] = useState<TaxExemptionRequestRow[]>([]);
  const [taxRequestsLoading, setTaxRequestsLoading] = useState(false);
  const [taxPendingCount, setTaxPendingCount] = useState(0);
  const [taxActionId, setTaxActionId] = useState<number | null>(null);

  // Walk-Ins tab state
  const [walkIns, setWalkIns] = useState<WalkInRow[]>([]);
  const [walkInsLoading, setWalkInsLoading] = useState(false);
  const [walkInStatusFilter, setWalkInStatusFilter] = useState<'all' | 'not_invited' | 'invited' | 'converted'>('all');
  const [walkInSort, setWalkInSort] = useState<'recent' | 'visits' | 'spend'>('recent');
  const [walkInDateRange, setWalkInDateRange] = useState<'30' | '90' | 'all'>('30');

  // Resend verification state
  const [resendingVerification, setResendingVerification] = useState<number | null>(null);
  const [resendResult, setResendResult] = useState<{ memberId: number; type: 'success' | 'error'; message: string } | null>(null);

  // Reset Password Modal
  const [resetModal, setResetModal] = useState<ResetPasswordModalState>({
    isOpen: false,
    member: null,
    loading: false,
    result: null,
  });
  const [copiedPassword, setCopiedPassword] = useState(false);

  // Delete Member Modal
  const [deleteModal, setDeleteModal] = useState<DeleteMemberModalState>({
    isOpen: false,
    member: null,
    loading: false,
    error: null,
  });

  // Member Account Modal (ledger: credit / bag grants / tab)
  const [accountModalMember, setAccountModalMember] = useState<BridgeClubMember | null>(null);

  // Edit Member Modal
  const [editModal, setEditModal] = useState<EditMemberModalState>({
    isOpen: false,
    member: null,
    loading: false,
    error: null,
    fieldErrors: {},
  });
  const [editForm, setEditForm] = useState<EditFormValues>({
    first_name: '', last_name: '', email: '', phone: '',
    zip_code: '', city: '', state: 'WA', home_location: '', local_access: false,
  });
  // Retail-location dropdown options (fetched once, cached across opens)
  const [retailLocations, setRetailLocations] = useState<RetailLocationOption[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState<string | null>(null);
  // Brief auto-dismissing success banner after a successful edit
  const [editSuccess, setEditSuccess] = useState<string | null>(null);

  // QR URLs expansion state
  const [expandedQrStore, setExpandedQrStore] = useState<number | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const copyQrUrl = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedUrl(label);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [analyticsRes, membersRes, storesRes] = await Promise.all([
        api.get('/admin/bridge-club/analytics/'),
        api.get('/admin/bridge-club/members/'),
        api.get('/admin/bridge-club/stores/'),
      ]);

      setAnalytics(analyticsRes.data);
      setMembers(membersRes.data);
      setStores(storesRes.data);
    } catch (err: any) {
      console.error('Failed to load Bridge Club data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Lazy-load local members when the user flips the Members-tab scope
  // toggle. Different endpoint shape from /admin/bridge-club/members/
  // (includes local_points.balance), so we keep it in a separate state.
  useEffect(() => {
    if (activeTab !== 'members' || membersScope !== 'local') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/admin/bridge-club/local-members/');
        if (!cancelled) setLocalMembers(res.data?.members ?? []);
      } catch (err) {
        console.error('Failed to load local members:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, membersScope]);

  // Tax exemption requests: pending count on mount (tab + nav badge parity),
  // full pending list when the tab is open. Wires the existing admin viewset.
  const loadTaxRequests = useCallback(async () => {
    setTaxRequestsLoading(true);
    try {
      const res = await api.get('/admin/tax-exemption-requests/?status=PENDING');
      setTaxRequests(res.data?.requests ?? []);
      setTaxPendingCount(res.data?.pending_count ?? 0);
    } catch (err) {
      console.error('Failed to load tax exemption requests:', err);
    } finally {
      setTaxRequestsLoading(false);
    }
  }, []);

  useEffect(() => { loadTaxRequests(); }, [loadTaxRequests]);
  useEffect(() => {
    if (activeTab === 'tax-requests') loadTaxRequests();
  }, [activeTab, loadTaxRequests]);

  const handleApproveTaxRequest = async (id: number) => {
    if (!window.confirm(
      'Approve this tax exemption? This creates an ACTIVE certificate on the ' +
      'member’s billing account and marks them tax-exempt.')) return;
    setTaxActionId(id);
    try {
      await api.post(`/admin/tax-exemption-requests/${id}/approve/`, {});
      await loadTaxRequests();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve request.');
    } finally {
      setTaxActionId(null);
    }
  };

  const handleRejectTaxRequest = async (id: number) => {
    const reason = window.prompt('Reason for rejection (the member will see this):');
    if (reason === null) return;                 // cancelled
    if (!reason.trim()) { alert('A reason is required to reject.'); return; }
    setTaxActionId(id);
    try {
      await api.post(
        `/admin/tax-exemption-requests/${id}/reject/`, { staff_notes: reason.trim() });
      await loadTaxRequests();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject request.');
    } finally {
      setTaxActionId(null);
    }
  };

  // Load walk-ins when tab becomes active OR filters change. The filter
  // params line up with backend admin_walk_ins_list.
  useEffect(() => {
    if (activeTab !== 'walk-ins') return;
    let cancelled = false;
    (async () => {
      setWalkInsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('status', walkInStatusFilter);
        params.set('sort', walkInSort);
        if (walkInDateRange !== 'all') {
          const since = new Date();
          since.setDate(since.getDate() - parseInt(walkInDateRange, 10));
          params.set('since', since.toISOString().slice(0, 10));
        }
        const res = await api.get(`/admin/bridge-club/walk-ins/?${params}`);
        if (!cancelled) setWalkIns(res.data?.visitors ?? []);
      } catch (err) {
        console.error('Failed to load walk-ins:', err);
      } finally {
        if (!cancelled) setWalkInsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, walkInStatusFilter, walkInSort, walkInDateRange]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchQuery ||
      member.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.member_number.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStore = !storeFilter ||
      member.linked_store_name?.toLowerCase().includes(storeFilter.toLowerCase());

    const matchesState = !stateFilter ||
      member.state.toLowerCase() === stateFilter.toLowerCase();

    const matchesVerification = !verificationFilter ||
      (verificationFilter === 'verified' && member.email_verified) ||
      (verificationFilter === 'unverified' && !member.email_verified);

    return matchesSearch && matchesStore && matchesState && matchesVerification;
  });

  const uniqueStates = Array.from(new Set(members.map(m => m.state))).sort();

  const openResetModal = (member: BridgeClubMember) => {
    setResetModal({
      isOpen: true,
      member,
      loading: false,
      result: null,
    });
    setCopiedPassword(false);
  };

  const closeResetModal = () => {
    setResetModal({
      isOpen: false,
      member: null,
      loading: false,
      result: null,
    });
    setCopiedPassword(false);
  };

  const handleResendVerification = async (member: BridgeClubMember) => {
    setResendingVerification(member.id);
    setResendResult(null);

    try {
      const response = await api.post(`/admin/bridge-club/members/${member.id}/resend-verification/`);
      setResendResult({
        memberId: member.id,
        type: 'success',
        message: response.data.message || 'Verification email sent!',
      });
    } catch (err: any) {
      setResendResult({
        memberId: member.id,
        type: 'error',
        message: err.response?.data?.error || err.response?.data?.detail || 'Failed to send verification email',
      });
    } finally {
      setResendingVerification(null);
      setTimeout(() => setResendResult(null), 5000);
    }
  };

  const handleGeneratePassword = async () => {
    if (!resetModal.member) return;

    setResetModal(prev => ({ ...prev, loading: true, result: null }));
    setCopiedPassword(false);

    try {
      const response = await api.post(
        `/admin/bridge-club/members/${resetModal.member.id}/set-password/`,
        { method: 'generate' }
      );

      setResetModal(prev => ({
        ...prev,
        loading: false,
        result: {
          type: 'success',
          message: 'Temporary password generated successfully!',
          temporaryPassword: response.data.temporary_password,
        },
      }));
    } catch (err: any) {
      setResetModal(prev => ({
        ...prev,
        loading: false,
        result: {
          type: 'error',
          message: err.response?.data?.error || 'Failed to generate password',
        },
      }));
    }
  };

  const handleSendResetEmail = async () => {
    if (!resetModal.member) return;

    setResetModal(prev => ({ ...prev, loading: true, result: null }));

    try {
      const response = await api.post(
        `/admin/bridge-club/members/${resetModal.member.id}/set-password/`,
        { method: 'email' }
      );

      setResetModal(prev => ({
        ...prev,
        loading: false,
        result: {
          type: 'success',
          message: response.data.message || 'Password reset email sent!',
        },
      }));
    } catch (err: any) {
      setResetModal(prev => ({
        ...prev,
        loading: false,
        result: {
          type: 'error',
          message: err.response?.data?.error || 'Failed to send reset email',
        },
      }));
    }
  };

  // Delete Member Modal functions
  const openDeleteModal = (member: BridgeClubMember) => {
    setDeleteModal({
      isOpen: true,
      member,
      loading: false,
      error: null,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      member: null,
      loading: false,
      error: null,
    });
  };

  const handleDeleteMember = async () => {
    if (!deleteModal.member) return;

    setDeleteModal(prev => ({ ...prev, loading: true, error: null }));

    try {
      await api.delete(`/admin/bridge-club/members/${deleteModal.member.id}/delete/`);

      // Remove member from local state
      setMembers(prev => prev.filter(m => m.id !== deleteModal.member!.id));

      // Update analytics if available
      if (analytics) {
        setAnalytics(prev => prev ? {
          ...prev,
          total_members: prev.total_members - 1,
        } : null);
      }

      closeDeleteModal();
    } catch (err: any) {
      setDeleteModal(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.error || 'Failed to delete member',
      }));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 3000);
    });
  };

  // Edit Member Modal functions
  const loadRetailLocations = async () => {
    setLocationsLoading(true);
    setLocationsError(null);
    try {
      const res = await api.get('/admin/retail-locations/');
      setRetailLocations(res.data ?? []);
    } catch (err) {
      console.error('Failed to load retail locations:', err);
      setLocationsError('Could not load store list.');
    } finally {
      setLocationsLoading(false);
    }
  };

  const openEditModal = (member: BridgeClubMember) => {
    setEditModal({ isOpen: true, member, loading: false, error: null, fieldErrors: {} });
    setEditForm({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      phone: member.phone || '',
      zip_code: member.zip_code || '',
      city: member.city || '',
      state: member.state || 'WA',
      home_location: member.home_location_id != null ? String(member.home_location_id) : '',
      local_access: !!member.local_access,
    });
    // Fetch the dropdown options once; reuse the cache on subsequent opens.
    if (retailLocations.length === 0 && !locationsLoading) {
      loadRetailLocations();
    }
  };

  const closeEditModal = () => {
    setEditModal({ isOpen: false, member: null, loading: false, error: null, fieldErrors: {} });
  };

  const handleSaveEdit = async () => {
    if (!editModal.member) return;

    // Client-side required-field validation.
    const clientErrors: Record<string, string> = {};
    if (!editForm.first_name.trim()) clientErrors.first_name = 'First name is required.';
    if (!editForm.last_name.trim()) clientErrors.last_name = 'Last name is required.';
    if (Object.keys(clientErrors).length > 0) {
      setEditModal(prev => ({ ...prev, fieldErrors: clientErrors, error: 'Please fix the highlighted fields.' }));
      return;
    }

    const memberId = editModal.member.id;
    setEditModal(prev => ({ ...prev, loading: true, error: null, fieldErrors: {} }));

    // Normalize before sending: lowercase/trim email, trim zip, null-or-int
    // home_location, boolean local_access. (Backend also strips text fields,
    // but normalizing here keeps the payload clean and predictable.)
    const payload = {
      first_name: editForm.first_name.trim(),
      last_name: editForm.last_name.trim(),
      email: editForm.email.trim().toLowerCase(),
      phone: editForm.phone.trim(),
      city: editForm.city.trim(),
      state: editForm.state.trim(),
      zip_code: editForm.zip_code.trim(),
      home_location: editForm.home_location === '' ? null : parseInt(editForm.home_location, 10),
      local_access: editForm.local_access,
    };

    try {
      const response = await api.post(
        `/admin/bridge-club/members/${memberId}/update/`,
        payload
      );
      const updated = response.data;
      // Replace the matching member with the refreshed record from the server.
      setMembers(prev => prev.map(m => (m.id === updated.id ? updated : m)));
      closeEditModal();
      setEditSuccess(`Updated ${updated.first_name} ${updated.last_name}`);
      setTimeout(() => setEditSuccess(null), 4000);
    } catch (err: any) {
      const statusCode = err?.response?.status;
      const respData = err?.response?.data;
      if (statusCode === 400 && respData && typeof respData === 'object') {
        // Backend per-field errors, e.g. {email: ['Already in use...']}
        const fieldErrors: Record<string, string> = {};
        Object.entries(respData).forEach(([key, val]) => {
          fieldErrors[key] = Array.isArray(val) ? String(val[0]) : String(val);
        });
        setEditModal(prev => ({ ...prev, fieldErrors, error: null }));
      } else if (statusCode === 403) {
        setEditModal(prev => ({ ...prev, error: "You don't have permission to edit this member." }));
      } else {
        console.error('Member edit save failed:', err);
        setEditModal(prev => ({ ...prev, error: 'Save failed. Please try again.' }));
      }
    } finally {
      setEditModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Inline styles for the edit form (kept local — consistent with the
  // inline-styled controls elsewhere in this file; no new CSS tokens).
  const editLabelStyle: React.CSSProperties = {
    display: 'block', fontSize: '0.8rem', fontWeight: 600,
    color: '#374151', marginBottom: 4,
  };
  const editInputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB',
    borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box',
  };
  const editRowStyle: React.CSSProperties = {
    display: 'flex', gap: 12, marginBottom: 14,
  };

  return (
    <div className="bridge-club-admin-page">
      <div className="page-header">
        <h1>Bridge Club Network</h1>
        <p className="page-subtitle">Network-wide Bridge Club management</p>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {editSuccess && (
        <div
          style={{
            background: '#D1FAE5', color: '#065F46', padding: '10px 16px',
            borderRadius: 8, marginBottom: 12, fontSize: '0.9rem', fontWeight: 500,
          }}
        >
          ✓ {editSuccess}
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          Members ({members.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'stores' ? 'active' : ''}`}
          onClick={() => setActiveTab('stores')}
        >
          Stores ({stores.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'walk-ins' ? 'active' : ''}`}
          onClick={() => setActiveTab('walk-ins')}
        >
          Walk-Ins{walkIns.length ? ` (${walkIns.length})` : ''}
        </button>
        <button
          className={`tab-btn ${activeTab === 'tax-requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('tax-requests')}
        >
          Tax Requests
          {taxPendingCount > 0 && (
            <span className="nav-badge" style={{
              marginLeft: 6, background: '#DC2626', color: '#fff',
              borderRadius: 10, padding: '1px 7px', fontSize: 12, fontWeight: 700,
            }}>{taxPendingCount}</span>
          )}
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading Bridge Club data...</div>
      ) : (
        <>
          {/* Overview Tab - no data */}
          {activeTab === 'overview' && !analytics && (
            <div className="overview-section">
              <div className="no-data-message">
                <h3>No Data Available</h3>
                <p>Bridge Club analytics will appear here once members start joining and making purchases.</p>
              </div>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && analytics && (
            <div className="overview-section">
              {/* Summary Cards */}
              <div className="summary-grid">
                <div className="summary-card clickable" onClick={() => setActiveTab('members')}>
                  <div className="summary-value">{analytics.total_members}</div>
                  <div className="summary-label">Total Members</div>
                </div>
                <div className="summary-card highlight-green clickable" onClick={() => setActiveTab('members')}>
                  <div className="summary-value">+{analytics.new_members_this_month}</div>
                  <div className="summary-label">New This Month</div>
                </div>
                <div className="summary-card clickable" onClick={() => setActiveTab('members')}>
                  <div className="summary-value">{analytics.active_members}</div>
                  <div className="summary-label">Active Members</div>
                </div>
                <div className="summary-card clickable" onClick={() => navigate('/admin/bridge-club/purchases')}>
                  <div className="summary-value">{analytics.total_purchases}</div>
                  <div className="summary-label">Total Purchases</div>
                </div>
                <div className="summary-card highlight-blue clickable" onClick={() => navigate('/admin/bridge-club/purchases')}>
                  <div className="summary-value">{analytics.purchases_this_month}</div>
                  <div className="summary-label">Purchases This Month</div>
                </div>
                <div className="summary-card clickable" onClick={() => navigate('/admin/bridge-club/rewards')}>
                  <div className="summary-value">{analytics.loyalty_redemptions}</div>
                  <div className="summary-label">Rewards Redeemed</div>
                </div>
                <div className="summary-card highlight-green clickable" onClick={() => navigate('/admin/bridge-club/credits')}>
                  <div className="summary-value">Credits</div>
                  <div className="summary-label">Store Credits</div>
                </div>
                <div className="summary-card highlight-gold clickable" onClick={() => navigate('/admin/bridge-club/analytics')}>
                  <div className="summary-value">{formatCurrency(analytics.total_revenue)}</div>
                  <div className="summary-label">Total Revenue</div>
                </div>
                <div className="summary-card clickable" onClick={() => navigate('/admin/bridge-club/analytics')}>
                  <div className="summary-value">{formatCurrency(analytics.revenue_this_month)}</div>
                  <div className="summary-label">Revenue This Month</div>
                </div>
              </div>

              {/* Laura Learning Insights */}
              <LauraInsightsCard />

              {/* Top Stores */}
              {analytics.top_stores.length > 0 && (
                <div className="card top-stores-card">
                  <h3>Top Stores by Members</h3>
                  <div className="top-stores-list">
                    {analytics.top_stores.map((store, index) => (
                      <div key={store.id} className="store-row">
                        <span className="rank">#{index + 1}</span>
                        <span className="store-name">{store.name}</span>
                        <span className="store-location">{store.city}, {store.state}</span>
                        <span className="member-count">{store.member_count} members</span>
                        <span className="revenue">{formatCurrency(store.total_revenue)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Products */}
              {analytics.top_products.length > 0 && (
                <div className="card top-products-card">
                  <h3>Top Red Bridge Products</h3>
                  <div className="products-list">
                    {analytics.top_products.map((product, index) => (
                      <div key={product.name} className="product-row">
                        <span className="rank">#{index + 1}</span>
                        <span className="product-name">{product.name}</span>
                        <span className="product-count">{product.count} sold</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Competitor Intelligence */}
              {analytics.competitor_analytics && analytics.competitor_analytics.total_competitor_items > 0 && (
                <div className="card competitor-card">
                  <h3>Competitor Intelligence</h3>
                  <div className="competitor-summary">
                    <div className="competitor-stat">
                      <span className="stat-value">{analytics.competitor_analytics.total_competitor_items}</span>
                      <span className="stat-label">Competitor Products Scanned</span>
                    </div>
                    <div className="competitor-stat">
                      <span className="stat-value">{formatCurrency(analytics.competitor_analytics.competitor_revenue)}</span>
                      <span className="stat-label">Competitor Spend Tracked</span>
                    </div>
                  </div>
                  {analytics.competitor_analytics.top_competitor_brands.length > 0 && (
                    <div className="competitor-brands">
                      <h4>Top Competitor Brands</h4>
                      {analytics.competitor_analytics.top_competitor_brands.map((brand, index) => (
                        <div key={brand.brand} className="brand-row">
                          <span className="rank">#{index + 1}</span>
                          <span className="brand-name">{brand.brand}</span>
                          <span className="brand-count">{brand.count} bags</span>
                          <span className="brand-revenue">{formatCurrency(brand.revenue)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="members-section">
              {/* Scope toggle — switches between the all-members endpoint
                  and the local-access-only endpoint that returns
                  local_points balances. */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 12 }}>
                <button
                  onClick={() => setMembersScope('all')}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #D1D5DB',
                    background: membersScope === 'all' ? '#2D5A27' : '#FFFFFF',
                    color: membersScope === 'all' ? '#FFFFFF' : '#374151',
                    borderRadius: '6px 0 0 6px',
                    cursor: 'pointer',
                    fontWeight: membersScope === 'all' ? 600 : 400,
                  }}
                >
                  All Members
                </button>
                <button
                  onClick={() => setMembersScope('local')}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #D1D5DB',
                    borderLeft: 'none',
                    background: membersScope === 'local' ? '#2D5A27' : '#FFFFFF',
                    color: membersScope === 'local' ? '#FFFFFF' : '#374151',
                    borderRadius: '0 6px 6px 0',
                    cursor: 'pointer',
                    fontWeight: membersScope === 'local' ? 600 : 400,
                  }}
                >
                  Local Only{localMembers.length ? ` (${localMembers.length})` : ''}
                </button>
              </div>

              {/* Local Members list — uses the local-members endpoint
                  shape (local_points.balance) and shows a green LOCAL
                  badge per row. Filters above don't apply in this mode. */}
              {membersScope === 'local' ? (
                localMembers.length === 0 ? (
                  <div className="no-results">No local members loaded yet.</div>
                ) : (
                  <table className="members-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Points Balance</th>
                        <th>Lifetime Earned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localMembers.map((m: any) => (
                        <tr
                          key={m.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/admin/bridge-club/members/${m.id}`)}
                        >
                          <td>
                            <strong>{m.name}</strong>
                            <span style={{ marginLeft: 8, background: '#D1FAE5', color: '#065F46', padding: '2px 6px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                              LOCAL
                            </span>
                          </td>
                          <td>{m.email || '—'}</td>
                          <td>{m.phone || '—'}</td>
                          <td>{m.local_points?.balance ?? 0} pts</td>
                          <td>{m.local_points?.lifetime_earned ?? 0} pts</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              ) : (
              <>
              {/* Filters */}
              <div className="filters-bar">
                <input
                  type="text"
                  placeholder="Search by name, email, or member #..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All States</option>
                  {uniqueStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Filter by store..."
                  value={storeFilter}
                  onChange={(e) => setStoreFilter(e.target.value)}
                  className="filter-input"
                />
                <select
                  value={verificationFilter}
                  onChange={(e) => setVerificationFilter(e.target.value as '' | 'verified' | 'unverified')}
                  className="filter-select"
                >
                  <option value="">All Members</option>
                  <option value="verified">Verified</option>
                  <option value="unverified">Unverified</option>
                </select>
              </div>

              <div className="members-count">
                Showing {filteredMembers.length} of {members.length} members
              </div>

              {/* Desktop Table */}
              <div className="table-container desktop-only">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Contact</th>
                      <th>Location</th>
                      <th>Store</th>
                      <th>Loyalty</th>
                      <th>Purchases</th>
                      <th>Last Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map(member => (
                      <tr key={member.id}>
                        <td>
                          <Link
                            to={`/admin/bridge-club/members/${member.id}`}
                            className="member-detail-link"
                            title="View member detail"
                            style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                          >
                            <div className="member-name">
                              {member.first_name} {member.last_name}
                            </div>
                            <div className="member-number">#{member.member_number}</div>
                          </Link>
                        </td>
                        <td>
                          <div className="member-email">
                            {member.email}
                            {!member.email_verified && <span className="unverified-badge">Unverified</span>}
                          </div>
                          <div className="member-phone">{member.phone}</div>
                        </td>
                        <td>
                          <span>{member.city}, {member.state}</span>
                        </td>
                        <td>
                          <span className={member.linked_store_name ? '' : 'no-store'}>
                            {member.linked_store_name || 'No store'}
                          </span>
                        </td>
                        <td>
                          {member.local_access ? (
                            /* Local members are on the POINTS program, not the
                               punch card — show their balance. */
                            <div className="loyalty-display">
                              <span style={{ fontWeight: 600, color: '#2D5A27' }}>
                                {(member.local_points_balance ?? 0).toLocaleString()} pts
                              </span>
                              <div className="loyalty-text" style={{ color: '#6B7280' }}>
                                {Math.floor((member.local_points_balance ?? 0) / 500)} reward
                                {Math.floor((member.local_points_balance ?? 0) / 500) === 1 ? '' : 's'} ($10 ea)
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="loyalty-display">
                                {[...Array(10)].map((_, i) => (
                                  <span
                                    key={i}
                                    className={`punch-dot ${i < member.loyalty_punches ? 'filled' : ''}`}
                                  />
                                ))}
                              </div>
                              <div className="loyalty-text">{member.loyalty_punches}/10</div>
                            </>
                          )}
                        </td>
                        <td>
                          <span className="purchase-amount">{formatCurrency(member.total_purchases)}</span>
                        </td>
                        <td>
                          <span>{formatDate(member.last_purchase_date)}</span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            {!member.email_verified && (
                              <button
                                className="action-btn resend-verification-btn"
                                onClick={() => handleResendVerification(member)}
                                disabled={resendingVerification === member.id}
                                title="Resend Verification Email"
                              >
                                {resendingVerification === member.id ? 'Sending...' : 'Resend Verify'}
                              </button>
                            )}
                            <button
                              className="action-btn edit-btn"
                              onClick={() => openEditModal(member)}
                              title="Edit Member"
                            >
                              Edit
                            </button>
                            <button
                              className="action-btn edit-btn"
                              onClick={() => setAccountModalMember(member)}
                              title="Account — credit, bag grants, tab"
                            >
                              Account
                            </button>
                            <button
                              className="action-btn reset-password-btn"
                              onClick={() => openResetModal(member)}
                              title="Reset Password"
                            >
                              Reset Password
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => openDeleteModal(member)}
                              title="Delete Account"
                            >
                              Delete
                            </button>
                          </div>
                          {resendResult && resendResult.memberId === member.id && (
                            <div className={`resend-feedback ${resendResult.type}`}>
                              {resendResult.message}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredMembers.length === 0 && (
                  <div className="no-results">
                    No members match your filters.
                  </div>
                )}
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards mobile-only">
                {filteredMembers.map(member => (
                  <div key={member.id} className="mobile-member-card">
                    <Link
                      to={`/admin/bridge-club/members/${member.id}`}
                      className="mobile-member-header member-detail-link"
                      title="View member detail"
                      style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer', display: 'block' }}
                    >
                      <div className="mobile-member-name">
                        {member.first_name} {member.last_name}
                      </div>
                      <div className="mobile-member-number">#{member.member_number}</div>
                    </Link>
                    <div className="mobile-member-details">
                      <div className="mobile-member-row">
                        <span className="mobile-label">📧</span>
                        <span className="mobile-value">
                          {member.email}
                          {!member.email_verified && <span className="unverified-badge">Unverified</span>}
                        </span>
                      </div>
                      {member.phone && (
                        <div className="mobile-member-row">
                          <span className="mobile-label">📱</span>
                          <span className="mobile-value">{member.phone}</span>
                        </div>
                      )}
                      <div className="mobile-member-row">
                        <span className="mobile-label">📍</span>
                        <span className="mobile-value">{member.city}, {member.state}</span>
                      </div>
                      <div className="mobile-member-row">
                        <span className="mobile-label">🏪</span>
                        <span className={`mobile-value ${!member.linked_store_name ? 'no-store' : ''}`}>
                          {member.linked_store_name || 'No store'}
                        </span>
                      </div>
                    </div>
                    <div className="mobile-member-stats">
                      <div className="mobile-member-stat">
                        {member.local_access ? (
                          <>
                            <div className="mobile-stat-value" style={{ color: '#2D5A27' }}>
                              {(member.local_points_balance ?? 0).toLocaleString()} pts
                            </div>
                            <div className="mobile-stat-label">
                              {Math.floor((member.local_points_balance ?? 0) / 500)} reward(s)
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="loyalty-display">
                              {[...Array(10)].map((_, i) => (
                                <span
                                  key={i}
                                  className={`punch-dot ${i < member.loyalty_punches ? 'filled' : ''}`}
                                />
                              ))}
                            </div>
                            <div className="mobile-stat-label">{member.loyalty_punches}/10 Punches</div>
                          </>
                        )}
                      </div>
                      <div className="mobile-member-stat">
                        <div className="mobile-stat-value purchase-amount">
                          {formatCurrency(member.total_purchases)}
                        </div>
                        <div className="mobile-stat-label">Total Purchases</div>
                      </div>
                    </div>
                    <div className="mobile-member-footer">
                      <span className="mobile-last-active">
                        Last active: {formatDate(member.last_purchase_date)}
                      </span>
                      <div className="mobile-action-buttons">
                        {!member.email_verified && (
                          <button
                            className="action-btn resend-verification-btn"
                            onClick={() => handleResendVerification(member)}
                            disabled={resendingVerification === member.id}
                          >
                            {resendingVerification === member.id ? 'Sending...' : 'Resend Verify'}
                          </button>
                        )}
                        <button
                          className="action-btn edit-btn"
                          onClick={() => openEditModal(member)}
                        >
                          Edit
                        </button>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => setAccountModalMember(member)}
                        >
                          Account
                        </button>
                        <button
                          className="action-btn reset-password-btn"
                          onClick={() => openResetModal(member)}
                        >
                          Reset Password
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => openDeleteModal(member)}
                        >
                          Delete
                        </button>
                      </div>
                      {resendResult && resendResult.memberId === member.id && (
                        <div className={`resend-feedback ${resendResult.type}`}>
                          {resendResult.message}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {filteredMembers.length === 0 && (
                  <div className="no-results">
                    No members match your filters.
                  </div>
                )}
              </div>
              </>
              )}
            </div>
          )}

          {/* Stores Tab */}
          {activeTab === 'stores' && (
            <div className="stores-section">
              {/* Desktop Table */}
              <div className="table-container desktop-only">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Store</th>
                      <th>Location</th>
                      <th>Members</th>
                      <th>Purchases</th>
                      <th>Revenue</th>
                      <th>QR Codes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.map(store => (
                      <React.Fragment key={store.id}>
                        <tr>
                          <td>
                            <span className="store-name-cell">{store.name}</span>
                          </td>
                          <td>
                            <span>{store.city}, {store.state}</span>
                          </td>
                          <td>
                            <span className="member-count-cell">{store.member_count}</span>
                          </td>
                          <td>
                            <span>{store.purchases_count}</span>
                          </td>
                          <td>
                            <span className="revenue-cell">{formatCurrency(store.total_revenue)}</span>
                          </td>
                          <td>
                            {store.qr_urls ? (
                              <button
                                className="qr-toggle-btn"
                                onClick={() => setExpandedQrStore(expandedQrStore === store.id ? null : store.id)}
                              >
                                {expandedQrStore === store.id ? '▼ Hide URLs' : '▶ Show URLs'}
                              </button>
                            ) : (
                              <span className="no-qr">No QR setup</span>
                            )}
                          </td>
                        </tr>
                        {expandedQrStore === store.id && store.qr_urls && (
                          <tr className="qr-urls-row">
                            <td colSpan={6}>
                              <div className="qr-urls-container">
                                <div className="qr-url-item">
                                  <span className="qr-label">🪧 Floor Sign:</span>
                                  <code>{store.qr_urls.floor}</code>
                                  <button
                                    className="copy-btn"
                                    onClick={() => copyQrUrl(store.qr_urls!.floor, `${store.id}-floor`)}
                                  >
                                    {copiedUrl === `${store.id}-floor` ? '✓' : '📋'}
                                  </button>
                                </div>
                                <div className="qr-url-item">
                                  <span className="qr-label">💵 Register:</span>
                                  <code>{store.qr_urls.register}</code>
                                  <button
                                    className="copy-btn"
                                    onClick={() => copyQrUrl(store.qr_urls!.register, `${store.id}-register`)}
                                  >
                                    {copiedUrl === `${store.id}-register` ? '✓' : '📋'}
                                  </button>
                                </div>
                                <div className="qr-url-item">
                                  <span className="qr-label">📦 Shelf:</span>
                                  <code>{store.qr_urls.shelf}</code>
                                  <button
                                    className="copy-btn"
                                    onClick={() => copyQrUrl(store.qr_urls!.shelf, `${store.id}-shelf`)}
                                  >
                                    {copiedUrl === `${store.id}-shelf` ? '✓' : '📋'}
                                  </button>
                                </div>
                                <div className="qr-url-item">
                                  <span className="qr-label">🔗 General:</span>
                                  <code>{store.qr_urls.general}</code>
                                  <button
                                    className="copy-btn"
                                    onClick={() => copyQrUrl(store.qr_urls!.general, `${store.id}-general`)}
                                  >
                                    {copiedUrl === `${store.id}-general` ? '✓' : '📋'}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="mobile-cards mobile-only">
                {stores.map(store => (
                  <div key={store.id} className="mobile-store-card">
                    <div className="mobile-store-header">
                      <span className="mobile-store-name">{store.name}</span>
                      <span className="mobile-store-location">{store.city}, {store.state}</span>
                    </div>
                    <div className="mobile-store-stats">
                      <div className="mobile-stat">
                        <span className="mobile-stat-value">{store.member_count}</span>
                        <span className="mobile-stat-label">Members</span>
                      </div>
                      <div className="mobile-stat">
                        <span className="mobile-stat-value">{store.purchases_count}</span>
                        <span className="mobile-stat-label">Purchases</span>
                      </div>
                      <div className="mobile-stat">
                        <span className="mobile-stat-value mobile-revenue">{formatCurrency(store.total_revenue)}</span>
                        <span className="mobile-stat-label">Revenue</span>
                      </div>
                    </div>
                    {store.qr_urls && (
                      <div className="mobile-qr-section">
                        <button
                          className="qr-toggle-btn"
                          onClick={() => setExpandedQrStore(expandedQrStore === store.id ? null : store.id)}
                        >
                          {expandedQrStore === store.id ? '▼ Hide QR URLs' : '▶ Show QR URLs'}
                        </button>
                        {expandedQrStore === store.id && (
                          <div className="mobile-qr-urls">
                            <div className="mobile-qr-item">
                              <span>🪧 Floor</span>
                              <button onClick={() => copyQrUrl(store.qr_urls!.floor, `${store.id}-floor`)}>
                                {copiedUrl === `${store.id}-floor` ? '✓ Copied' : 'Copy'}
                              </button>
                            </div>
                            <div className="mobile-qr-item">
                              <span>💵 Register</span>
                              <button onClick={() => copyQrUrl(store.qr_urls!.register, `${store.id}-register`)}>
                                {copiedUrl === `${store.id}-register` ? '✓ Copied' : 'Copy'}
                              </button>
                            </div>
                            <div className="mobile-qr-item">
                              <span>📦 Shelf</span>
                              <button onClick={() => copyQrUrl(store.qr_urls!.shelf, `${store.id}-shelf`)}>
                                {copiedUrl === `${store.id}-shelf` ? '✓ Copied' : 'Copy'}
                              </button>
                            </div>
                            <div className="mobile-qr-item">
                              <span>🔗 General</span>
                              <button onClick={() => copyQrUrl(store.qr_urls!.general, `${store.id}-general`)}>
                                {copiedUrl === `${store.id}-general` ? '✓ Copied' : 'Copy'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {stores.length === 0 && (
                <div className="no-results">
                  No stores with Bridge Club members yet.
                </div>
              )}
            </div>
          )}

          {/* Walk-Ins Tab — UnmatchedVisitor rows built from Helcim
              receipts for cardholders who haven't joined Bridge Club. */}
          {activeTab === 'walk-ins' && (
            <div className="members-section">
              <div className="filters-bar" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <select
                  value={walkInStatusFilter}
                  onChange={(e) => setWalkInStatusFilter(e.target.value as typeof walkInStatusFilter)}
                  className="filter-select"
                >
                  <option value="all">All walk-ins</option>
                  <option value="not_invited">Not invited yet</option>
                  <option value="invited">Invited (not yet joined)</option>
                  <option value="converted">Joined Bridge Club</option>
                </select>
                <select
                  value={walkInDateRange}
                  onChange={(e) => setWalkInDateRange(e.target.value as typeof walkInDateRange)}
                  className="filter-select"
                >
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="all">All time</option>
                </select>
                <select
                  value={walkInSort}
                  onChange={(e) => setWalkInSort(e.target.value as typeof walkInSort)}
                  className="filter-select"
                >
                  <option value="recent">Most recent</option>
                  <option value="visits">Most visits</option>
                  <option value="spend">Highest spend</option>
                </select>
              </div>

              {walkInsLoading ? (
                <div className="loading-state">Loading walk-ins…</div>
              ) : walkIns.length === 0 ? (
                <div className="no-results">No walk-ins match this filter.</div>
              ) : (
                <table className="members-table">
                  <thead>
                    <tr>
                      <th>Cardholder</th>
                      <th>Card</th>
                      <th>Visits</th>
                      <th>Total Spend</th>
                      <th>Last Visit</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {walkIns.map((v) => (
                      <tr
                        key={v.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/bridge-club/walk-ins/${v.id}`)}
                      >
                        <td>
                          <strong>{v.cardholder_name}</strong>
                          <span style={{ marginLeft: 8, fontSize: 11, color: '#92400E', fontWeight: 600 }}>
                            WALK-IN
                          </span>
                        </td>
                        <td>
                          {v.card_type ? `${v.card_type} ` : ''}
                          {v.card_last_four ? `···· ${v.card_last_four}` : '—'}
                        </td>
                        <td>{v.visit_count}</td>
                        <td>${v.total_spend}</td>
                        <td>{v.last_visit ? formatDate(v.last_visit) : '—'}</td>
                        <td>
                          {v.promoted_to_member_id ? (
                            <span style={{ background: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                              Member
                            </span>
                          ) : v.invite_sent_at ? (
                            <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                              Invited
                            </span>
                          ) : (
                            <span style={{ background: '#F3F4F6', color: '#6B7280', padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>
                              Not invited
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Tax Requests Tab — pending Bridge Club tax-exemption requests.
              Approve calls the existing approve() flow (creates the
              TaxExemptionCertificate on the linked Customer); Reject captures a
              member-visible reason. */}
          {activeTab === 'tax-requests' && (
            <div className="members-section">
              {taxRequestsLoading ? (
                <div className="loading-state">Loading tax exemption requests…</div>
              ) : taxRequests.length === 0 ? (
                <div className="no-results">No pending tax exemption requests.</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Member</th>
                        <th>Submitted</th>
                        <th>Type</th>
                        <th>Certificate #</th>
                        <th>State</th>
                        <th>Expires</th>
                        <th>Certificate</th>
                        <th>Member Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxRequests.map(req => (
                        <tr key={req.id}>
                          <td>
                            <div className="member-name">{req.member_name}</div>
                            <div className="member-number">{req.member_email}</div>
                          </td>
                          <td>{formatDate(req.created_at)}</td>
                          <td>{req.exemption_type_display}</td>
                          <td>{req.certificate_number || '—'}</td>
                          <td>{req.issuing_state}</td>
                          <td>{req.expiration_date ? formatDate(req.expiration_date) : '—'}</td>
                          <td>
                            {req.certificate_url ? (
                              <a href={req.certificate_url} target="_blank" rel="noopener noreferrer">
                                {req.original_filename || 'View file'}
                              </a>
                            ) : '—'}
                          </td>
                          <td style={{ maxWidth: 220, whiteSpace: 'normal' }}>
                            {req.member_notes || '—'}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="action-btn edit-btn"
                                disabled={taxActionId === req.id}
                                onClick={() => handleApproveTaxRequest(req.id)}
                                title="Approve and create certificate"
                              >
                                {taxActionId === req.id ? '…' : 'Approve'}
                              </button>
                              <button
                                className="action-btn delete-btn"
                                disabled={taxActionId === req.id}
                                onClick={() => handleRejectTaxRequest(req.id)}
                                title="Reject with a reason"
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Reset Password Modal */}
      {resetModal.isOpen && resetModal.member && (
        <div className="modal-overlay" onClick={closeResetModal}>
          <div className="modal-content reset-password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button className="modal-close" onClick={closeResetModal}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="member-info-box">
                <strong>{resetModal.member.first_name} {resetModal.member.last_name}</strong>
                <span className="member-email-display">{resetModal.member.email}</span>
                <span className="member-number-display">#{resetModal.member.member_number}</span>
              </div>

              {!resetModal.result && (
                <div className="reset-options">
                  <p className="reset-description">Choose how to reset this member's password:</p>

                  <button
                    className="reset-option-btn generate-btn"
                    onClick={handleGeneratePassword}
                    disabled={resetModal.loading}
                  >
                    <span className="option-icon">&#128273;</span>
                    <div className="option-text">
                      <strong>Generate Temporary Password</strong>
                      <span>Create a password you can share with the member</span>
                    </div>
                  </button>

                  <button
                    className="reset-option-btn email-btn"
                    onClick={handleSendResetEmail}
                    disabled={resetModal.loading}
                  >
                    <span className="option-icon">&#9993;</span>
                    <div className="option-text">
                      <strong>Send Reset Email</strong>
                      <span>Member will receive an email with a reset link</span>
                    </div>
                  </button>

                  {resetModal.loading && (
                    <div className="reset-loading">Processing...</div>
                  )}
                </div>
              )}

              {resetModal.result && (
                <div className={`reset-result ${resetModal.result.type}`}>
                  {resetModal.result.type === 'error' && (
                    <div className="result-error">
                      <span className="result-icon">&#10060;</span>
                      <p>{resetModal.result.message}</p>
                      <button className="try-again-btn" onClick={() => setResetModal(prev => ({ ...prev, result: null }))}>
                        Try Again
                      </button>
                    </div>
                  )}

                  {resetModal.result.type === 'success' && resetModal.result.temporaryPassword && (
                    <div className="result-success">
                      <span className="result-icon">&#9989;</span>
                      <p>{resetModal.result.message}</p>
                      <div className="temp-password-box">
                        <label>Temporary Password:</label>
                        <div className="password-display">
                          <code>{resetModal.result.temporaryPassword}</code>
                          <button
                            className="copy-btn"
                            onClick={() => copyToClipboard(resetModal.result!.temporaryPassword!)}
                          >
                            {copiedPassword ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                      </div>
                      <p className="security-note">
                        Share this password securely with the member. They should change it after logging in.
                      </p>
                    </div>
                  )}

                  {resetModal.result.type === 'success' && !resetModal.result.temporaryPassword && (
                    <div className="result-success">
                      <span className="result-icon">&#9989;</span>
                      <p>{resetModal.result.message}</p>
                      <p className="email-sent-note">
                        The member will receive an email with instructions to reset their password.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="modal-close-btn" onClick={closeResetModal}>
                {resetModal.result ? 'Done' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Member Modal */}
      {deleteModal.isOpen && deleteModal.member && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content delete-member-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Member Account</h2>
              <button className="modal-close" onClick={closeDeleteModal}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="member-info-box">
                <strong>{deleteModal.member.first_name} {deleteModal.member.last_name}</strong>
                <span className="member-email-display">{deleteModal.member.email}</span>
                <span className="member-number-display">#{deleteModal.member.member_number}</span>
              </div>

              <div className="delete-warning">
                <p className="warning-text">
                  Are you sure you want to delete this member's account?
                </p>
                <p className="warning-subtext">This will permanently remove:</p>
                <ul className="delete-list">
                  <li>Their profile and login credentials</li>
                  <li>All linked animals ({deleteModal.member.animals_count} animals)</li>
                  <li>Feed bin data and history</li>
                  <li>Purchase history ({formatCurrency(deleteModal.member.total_purchases)})</li>
                  <li>Loyalty punches ({deleteModal.member.loyalty_punches}/10)</li>
                </ul>
                <p className="warning-final">
                  ⚠️ This action cannot be undone.
                </p>
              </div>

              {deleteModal.error && (
                <div className="delete-error">
                  {deleteModal.error}
                </div>
              )}
            </div>

            <div className="modal-footer delete-footer">
              <button
                className="modal-close-btn"
                onClick={closeDeleteModal}
                disabled={deleteModal.loading}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-btn"
                onClick={handleDeleteMember}
                disabled={deleteModal.loading}
              >
                {deleteModal.loading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {accountModalMember && (
        <MemberAccountModal
          member={accountModalMember}
          onClose={() => setAccountModalMember(null)}
        />
      )}

      {editModal.isOpen && editModal.member && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content edit-member-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Member</h2>
              <button className="modal-close" onClick={closeEditModal}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="member-info-box">
                <strong>{editModal.member.first_name} {editModal.member.last_name}</strong>
                <span className="member-number-display">#{editModal.member.member_number}</span>
              </div>

              {editModal.error && (
                <div className="delete-error" style={{ marginBottom: 14 }}>{editModal.error}</div>
              )}

              {/* Row 1: first / last name */}
              <div style={editRowStyle}>
                <div style={{ flex: 1 }}>
                  <label style={editLabelStyle}>First Name *</label>
                  <input
                    type="text"
                    style={{
                      ...editInputStyle,
                      borderColor: editModal.fieldErrors.first_name ? '#DC2626' : '#D1D5DB',
                    }}
                    value={editForm.first_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                  />
                  {editModal.fieldErrors.first_name && (
                    <div style={{ color: '#DC2626', fontSize: '0.75rem', marginTop: 4 }}>
                      {editModal.fieldErrors.first_name}
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={editLabelStyle}>Last Name *</label>
                  <input
                    type="text"
                    style={{
                      ...editInputStyle,
                      borderColor: editModal.fieldErrors.last_name ? '#DC2626' : '#D1D5DB',
                    }}
                    value={editForm.last_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                  />
                  {editModal.fieldErrors.last_name && (
                    <div style={{ color: '#DC2626', fontSize: '0.75rem', marginTop: 4 }}>
                      {editModal.fieldErrors.last_name}
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2: email */}
              <div style={{ marginBottom: 14 }}>
                <label style={editLabelStyle}>Email</label>
                <input
                  type="email"
                  style={{
                    ...editInputStyle,
                    borderColor: editModal.fieldErrors.email ? '#DC2626' : '#D1D5DB',
                  }}
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                />
                {editModal.fieldErrors.email && (
                  <div style={{ color: '#DC2626', fontSize: '0.75rem', marginTop: 4 }}>
                    {editModal.fieldErrors.email}
                  </div>
                )}
              </div>

              {/* Row 3: phone / zip */}
              <div style={editRowStyle}>
                <div style={{ flex: 2 }}>
                  <label style={editLabelStyle}>Phone</label>
                  <input
                    type="tel"
                    style={editInputStyle}
                    value={editForm.phone}
                    onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={editLabelStyle}>ZIP</label>
                  <input
                    type="text"
                    style={editInputStyle}
                    value={editForm.zip_code}
                    onChange={(e) => setEditForm(prev => ({ ...prev, zip_code: e.target.value }))}
                  />
                </div>
              </div>

              {/* Row 4: city / state */}
              <div style={editRowStyle}>
                <div style={{ flex: 2 }}>
                  <label style={editLabelStyle}>City</label>
                  <input
                    type="text"
                    style={editInputStyle}
                    value={editForm.city}
                    onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={editLabelStyle}>State</label>
                  <input
                    type="text"
                    style={editInputStyle}
                    value={editForm.state}
                    onChange={(e) => setEditForm(prev => ({ ...prev, state: e.target.value }))}
                  />
                </div>
              </div>

              {/* Row 5: home location */}
              <div style={{ marginBottom: 14 }}>
                <label style={editLabelStyle}>Home Store</label>
                {locationsLoading ? (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', padding: '8px 0' }}>
                    Loading stores…
                  </div>
                ) : locationsError ? (
                  <div style={{ fontSize: '0.875rem', color: '#DC2626', padding: '4px 0' }}>
                    {locationsError}{' '}
                    <button
                      type="button"
                      onClick={loadRetailLocations}
                      style={{ marginLeft: 6, fontSize: '0.8rem', cursor: 'pointer' }}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <select
                    style={editInputStyle}
                    value={editForm.home_location}
                    onChange={(e) => setEditForm(prev => ({ ...prev, home_location: e.target.value }))}
                  >
                    <option value="">— None —</option>
                    {retailLocations.map(loc => (
                      <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                    ))}
                  </select>
                )}
                {editModal.fieldErrors.home_location && (
                  <div style={{ color: '#DC2626', fontSize: '0.75rem', marginTop: 4 }}>
                    {editModal.fieldErrors.home_location}
                  </div>
                )}
              </div>

              {/* Row 6: local access */}
              <div style={{ marginBottom: 4 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editForm.local_access}
                    onChange={(e) => setEditForm(prev => ({ ...prev, local_access: e.target.checked }))}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                    Local Bridge Club member (access to mill, locker pickup, points-based rewards)
                  </span>
                </label>
              </div>
            </div>

            <div className="modal-footer delete-footer">
              <button
                className="modal-close-btn"
                onClick={closeEditModal}
                disabled={editModal.loading}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-btn"
                style={{ background: '#2D5A27' }}
                onClick={handleSaveEdit}
                disabled={editModal.loading}
              >
                {editModal.loading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BridgeClubAdmin;
